/* eslint-disable no-unused-vars */
import { useState, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSignal, useComputed } from '@preact/signals-react';
import { campaignReportsActions } from 'store/actions';
import { campaignReportsRequestTypes } from 'constants';

const fieldForDispatch = { Apps: 'selectedSearchApps', Country: 'selectedCountries' };

const getInitialSelectedAppsOrCountries = ({
  currentRequestType,
  selectedSearchApps,
  selectedCountries,
}) => {
  if (currentRequestType === campaignReportsRequestTypes.Applications) return selectedSearchApps;
  if (currentRequestType === campaignReportsRequestTypes.Country) return selectedCountries;
  return [];
};

export const useFacade = (currentRequestType) => {
  const updateTrigger$ = useSignal(0);
  const selectedSearchApps = useSelector((state) => state.campaignReports.selectedSearchApps);
  const selectedCountries = useSelector((state) => state.campaignReports.selectedCountries);
  const dispatch = useDispatch();

  const selectedAppsOrCountries$ = useSignal(
    new Set(
      getInitialSelectedAppsOrCountries({
        currentRequestType,
        selectedSearchApps,
        selectedCountries,
      }),
    ),
  );

  useEffect(() => {
    selectedAppsOrCountries$.value = new Set(
      getInitialSelectedAppsOrCountries({
        currentRequestType,
        selectedSearchApps,
        selectedCountries,
      }),
    );
  }, [currentRequestType]);

  const isActive = useCallback((appOrCountryId) =>
    selectedAppsOrCountries$.value.has(appOrCountryId),
  );

  const toggle = useCallback(
    (appOrCountryId) => {
      if (isActive(appOrCountryId)) {
        selectedAppsOrCountries$.value.delete(appOrCountryId);
      } else {
        selectedAppsOrCountries$.value.add(appOrCountryId);
      }
      dispatch(
        campaignReportsActions.updateProperty({
          propField: fieldForDispatch[currentRequestType],
          newValue: [...selectedAppsOrCountries$.value],
        }),
      );
      updateTrigger$.value += 1;
    },
    [isActive, currentRequestType],
  );

  const toggleAllOff = useCallback(() => {
    selectedAppsOrCountries$.value.clear();
    dispatch(
      campaignReportsActions.updateProperty({
        propField: fieldForDispatch[currentRequestType],
        newValue: [],
      }),
    );

    updateTrigger$.value += 1;
  }, [currentRequestType]);

  return {
    selectedAppsOrCountries$,
    isActive,
    toggle,
    toggleAllOff,
    updateTrigger$,
  };
};
